/**
 * The working table: sortable headers, per-column filters, a column picker.
 *
 * This is the lead table, generalised. It was written for leads, it worked, and
 * the events list had grown a second, worse implementation of the same idea —
 * three fixed dropdowns above the table, no column picker, its own sort. Rather
 * than teach the second one the tricks the first already knew, the first one
 * moved here and the second was deleted.
 *
 * Generic over the column key so each feature keeps its own closed set of
 * columns and its own `Record<K, ColumnMeta>`; nothing here knows what a lead or
 * an event is. What a feature supplies is a list of rows already reduced to
 * `title`, `subtitle` and rendered cells — the mapping from domain object to row
 * stays in the feature, where the domain knowledge lives.
 *
 * ## Below 760px it is not a table
 *
 * Seven columns at their real widths need about 1,100px and a phone gives 350,
 * so the same rows render as cards. That breakpoint lives here rather than in
 * each screen: five workspaces each calling `useIsMobile` and building their own
 * card list is five card layouts that drift apart, and it is the reason the
 * design's one card recipe had five spellings.
 */
import type { ReactNode } from 'react'
import { useIsMobile } from '@/shared/nav/useIsMobile'
import { RecordCards, type RecordCard } from '@/shared/ui/RecordCards'
import './DataTable.css'

/**
 * The fallback when a screen has not written its own.
 *
 * Decision 23 bans naming the absence — "No results" and "Nothing here" say only
 * that the list is empty, which the reader can already see. A screen should
 * always pass its own `emptyNote` naming the cause and what fills it; this
 * exists so that forgetting to is a vague sentence rather than a banned one.
 */
const DEFAULT_EMPTY = 'Nothing to show here yet. This list fills as records are added.'

export interface ColumnMeta {
  label: string
  /** The column's `fr` share of the grid. */
  width: number
}

/**
 * A rendered cell: a coloured pill, a single line of muted text, or a mark.
 *
 * The third case arrived with the publishing plan, whose Channels column is six
 * dots in a fixed order — the alignment down the column *is* the column, and no
 * arrangement of words reproduces it. `node` renders in place of the text, and
 * `text` is still required because the phone card and the tooltip need
 * something to say: a card that shows six dots with no context is a puzzle.
 *
 * Keep this narrow. A cell that can be anything is a table that can look like
 * anything, and the reason five screens read alike is that they cannot.
 */
export interface Cell<K extends string> {
  key: K
  badge: boolean
  text: string
  bg?: string
  color?: string
  /** Tooltip — worth setting where the text is likely to be clipped. */
  title?: string
  /** A mark rather than a word: the channel dots, a link, an icon and a count. */
  node?: ReactNode
  /**
   * Set where the figure is a fact about this row rather than about its column.
   *
   * Decision 78's italic clause: a price per share that *this holder* agreed,
   * rather than the round's own price, is drawn italic so a column of prices
   * shows at a glance which of them were negotiated. It is one bit rather than a
   * style hook — `node` is there for anything richer, and this stays narrow on
   * purpose.
   */
  italic?: boolean
  /**
   * A muted second line under the figure — decision 79.
   *
   * "150 subscribed, not issued" under a holding of nothing. It exists because
   * the two numbers must never be added: interest and issued shares are kept in
   * separate arrays on the record for exactly that reason, and a column that
   * showed one figure would have to choose which lie to tell. Two lines tell
   * neither.
   */
  sub?: string
  /**
   * A monospaced figure — decision 77, checked by A4.16 "wherever it appears".
   *
   * For a value whose *digits have to line up* to be read: a share series is a
   * range of share numbers, and `17106–30000` in a proportional face reads as a
   * phone number.
   */
  mono?: boolean
}

export interface DataRow<K extends string> {
  id: string
  title: string
  /** The second line under the title: region, category, whatever identifies it. */
  subtitle: string
  /** A logo or icon before the title. */
  leading?: ReactNode
  /** Highest severity marker on the row, or null when it has none. */
  flag?: 'critical' | 'warning' | null
  cells: Cell<K>[]
  /** Dims the row without hiding it — a past event, an archived lead. */
  muted?: boolean
  /** Rendered in a trailing column. Clicks inside it never open the row. */
  actions?: ReactNode
  /**
   * What the phone card shows, where the derived default is not right.
   *
   * The default reads the row: the first badge cell becomes the status pill and
   * the first two plain cells become the meta lines. That is usually what the
   * card wants, but it is at the mercy of the desktop column choice, and a
   * feature that has a better sentence than a truncated cell — dates joined with
   * a venue, an owner's name — supplies it here.
   */
  card?: {
    status?: { label: string; bg: string; color: string } | null
    /** Falsy entries are dropped, so `cond && text` is safe. */
    lines?: (string | false | null | undefined)[]
  }
}

/**
 * The row that closes the table — decision 81.
 *
 * Supplied by the feature, not computed here: which columns are money, which are
 * counts and which are words is domain knowledge, and 81's rule that *a column
 * of words is left blank rather than showing a count of something else* is only
 * checkable where the columns mean something.
 *
 * `label` carries its own scope, because a totals row that silently totals a
 * filtered table is a wrong number with a right name: "Total · 7 of 144 holders
 * shown".
 */
export interface TotalsRow<K extends string> {
  label: string
  cells: Cell<K>[]
}

/** Selectable values for one filterable column, in display order. */
export type FilterOptions<K extends string> = Partial<Record<K, { value: string; label: string }[]>>

/** Selected values per filterable column. An empty or absent list means "all". */
export type ColumnFilters<K extends string> = Partial<Record<K, string[]>>

export type SortDir = 'asc' | 'desc'

interface Props<K extends string> {
  meta: Record<K, ColumnMeta>
  /** Which columns are on, in the order they should appear. */
  columns: K[]
  rows: DataRow<K>[]

  /**
   * The identity column, which is always shown and always first.
   *
   * It is the only column that renders two lines, so it carries no width entry
   * and cannot be switched off — a table of rows you cannot name is not a table.
   */
  identityLabel: string
  identitySortKey: string
  identityWidth?: string

  sortBy: string
  sortDir: SortDir
  onSort: (key: string) => void

  /** Columns whose header carries a multi-select filter. */
  filterable: K[]
  filters: ColumnFilters<K>
  filterOptions: FilterOptions<K>
  onToggleFilter: (key: K, value: string) => void
  onClearFilter: (key: K) => void
  /** Which column's filter dropdown is open, if any. */
  openFilter: K | null
  onOpenFilter: (key: K | null) => void

  selectedId?: string | null
  onSelect: (id: string) => void

  loading?: boolean
  loadingNote?: string
  /** Shown when nothing is visible — say *why*, not "no results". */
  emptyNote?: ReactNode
  /** Closes the table with the sums of the rows on screen — decision 81. */
  totals?: TotalsRow<K> | null
  /** Width of the trailing actions column; only rendered when a row has actions. */
  actionsWidth?: string

  /**
   * The row whose detail is open underneath it, and what to draw there.
   *
   * The Sales CRM opens a lead inside the table rather than in a drawer over
   * it, so the list stays on screen while one row is being worked. Both props
   * are needed for anything to happen: a table that passes neither behaves
   * exactly as it did before, which is every other table in the app.
   *
   * Phones never expand. The panel is a two-column layout that would be a very
   * long single column on a narrow screen, and the card list already opens a
   * full-screen drawer — so the mobile branch below returns before this.
   */
  expandedId?: string | null
  renderExpanded?: (id: string) => ReactNode
}

function arrow(key: string, sortBy: string, sortDir: SortDir): string {
  if (key !== sortBy) return ''
  return sortDir === 'asc' ? ' ↑' : ' ↓'
}

/**
 * One row as a card.
 *
 * A badge cell without colours is still a badge — it gets the neutral pill
 * rather than being dropped, because the alternative is a card that silently
 * loses the one field the row is sorted on.
 */
function toCard<K extends string>(row: DataRow<K>, onSelect: (id: string) => void): RecordCard {
  const badge = row.cells.find((c) => c.badge)
  const derived = badge
    ? {
        label: badge.text,
        bg: badge.bg ?? 'var(--color-surface-muted)',
        color: badge.color ?? 'var(--color-text-muted)',
      }
    : undefined
  const status = row.card?.status === undefined ? derived : (row.card.status ?? undefined)

  return {
    id: row.id,
    title: row.title,
    subtitle: row.subtitle,
    leading: row.leading,
    status,
    lines: row.card?.lines ?? row.cells.filter((c) => !c.badge).slice(0, 2).map((c) => c.text),
    onOpen: () => onSelect(row.id),
  }
}

export function DataTable<K extends string>({
  meta,
  columns,
  rows,
  identityLabel,
  identitySortKey,
  identityWidth = '1.8fr',
  sortBy,
  sortDir,
  onSort,
  filterable,
  filters,
  filterOptions,
  onToggleFilter,
  onClearFilter,
  openFilter,
  onOpenFilter,
  selectedId = null,
  onSelect,
  loading = false,
  loadingNote = 'Loading…',
  emptyNote,
  totals = null,
  actionsWidth = '128px',
  expandedId = null,
  renderExpanded,
}: Props<K>) {
  const isMobile = useIsMobile()

  // Cards, not columns. Row actions are deliberately not carried across: they
  // are 11px buttons inside a row that is now the whole tap target, and the
  // drawer a tap away has the same actions at a size a thumb can hit.
  if (isMobile) {
    return (
      <>
        <RecordCards
          empty={loading ? loadingNote : (emptyNote ?? DEFAULT_EMPTY)}
          cards={loading ? [] : rows.map((row) => toCard(row, onSelect))}
        />
        {/* A card has no columns to close, so the sums become one sentence
            rather than being dropped: the figure is the reason the row exists,
            and a phone reading a share register still needs the total. */}
        {totals && !loading && rows.length > 0 && (
          <p className="datatable__foot datatable__foot--cards">
            <strong>{totals.label}</strong>
            {totals.cells
              .filter((c) => c.text)
              .map((c) => ` · ${meta[c.key].label} ${c.text}${c.sub ? ` (${c.sub})` : ''}`)
              .join('')}
          </p>
        )}
      </>
    )
  }

  // The actions column only exists while there is something to action, so a
  // normal working list keeps every pixel for data.
  const showActions = rows.some((r) => r.actions)

  /**
   * Columns share the space when there is space, and stop shrinking when there
   * is not — off-script 10 from the 24 Aug run.
   *
   * Bare `fr` units divide whatever width exists, however little each column
   * gets. At the All-columns width that put fourteen columns into a laptop's
   * viewport and crushed every one to about five characters, which is a table
   * of ellipses. Per-round columns (81) made it worse, because a round adds
   * three more.
   *
   * `minmax(floor, fr)` keeps the sharing and adds a floor. Once the floors add
   * up to more than the frame, the grid overflows and `.datatable` scrolls
   * sideways — a wide table you can scroll beats a narrow table you cannot read.
   * The floor is derived from the column's own weight so a Name column still
   * gets more room than an Org.nr, with a hard minimum for the narrow ones.
   */
  const floorOf = (k: K) => Math.max(76, Math.round(meta[k].width * 82))
  const gridTemplate = [
    `minmax(170px, ${identityWidth})`,
    ...columns.map((k) => `minmax(${floorOf(k)}px, ${meta[k].width}fr)`),
    ...(showActions ? [actionsWidth] : []),
  ].join(' ')
  /**
   * Head and body are separate grids, so both need the same minimum or they
   * scroll independently and the header stops sitting over its own column.
   */
  const minWidth =
    170 +
    columns.reduce((n, k) => n + floorOf(k), 0) +
    (showActions ? parseInt(actionsWidth, 10) || 0 : 0) +
    // The 10px gap between every pair of tracks, plus the row's own padding.
    (columns.length + (showActions ? 1 : 0)) * 10 +
    32

  const headers: { key: string; label: string; col: K | null }[] = [
    { key: identitySortKey, label: identityLabel, col: null },
    ...columns.map((k) => ({ key: k as string, label: meta[k].label, col: k })),
  ]

  return (
    <div className="datatable">
      <div
        className="datatable__head"
        style={{ gridTemplateColumns: gridTemplate, minWidth }}
      >
        {headers.map((h) => {
          const key = h.col
          const canFilter = key !== null && filterable.includes(key)
          const selected = (key !== null ? filters[key] : undefined) ?? []
          return (
            <div key={h.key} className="datatable__thwrap">
              <button
                className="datatable__th"
                onClick={() => onSort(h.key)}
                aria-sort={
                  h.key === sortBy ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                }
              >
                {h.label}
                {arrow(h.key, sortBy, sortDir)}
              </button>

              {canFilter && (
                <>
                  <button
                    className={`datatable__filterbtn${selected.length ? ' datatable__filterbtn--on' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenFilter(openFilter === key ? null : key)
                    }}
                    aria-expanded={openFilter === key}
                    aria-label={`Filter by ${h.label}`}
                  >
                    ▾
                  </button>

                  {openFilter === key && (
                    <div className="colfilter" onClick={(e) => e.stopPropagation()}>
                      {(filterOptions[key] ?? []).map((opt) => (
                        <label key={opt.value} className="colfilter__item">
                          <input
                            type="checkbox"
                            checked={selected.includes(opt.value)}
                            onChange={() => onToggleFilter(key, opt.value)}
                          />
                          {opt.label}
                        </label>
                      ))}
                      {(filterOptions[key] ?? []).length === 0 && (
                        <div className="colfilter__empty">Nothing to filter on yet.</div>
                      )}
                      <button className="colfilter__clear" onClick={() => onClearFilter(key)}>
                        Clear
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )
        })}
        {showActions && <div className="datatable__thwrap" />}
      </div>

      <div className="datatable__body" style={{ minWidth }}>
        {loading && <div className="datatable__empty">{loadingNote}</div>}

        {!loading &&
          rows.map((row) => {
            const expanded = !!renderExpanded && row.id === expandedId
            return (
            /* The rule between rows belongs to this wrapper, not to the row.
               An open row and its panel are one thing, so a line drawn under
               the row would cut the detail off from the lead it describes. */
            <div className="datatable__rowgroup" key={row.id}>
            <div
              className={
                'datatable__row' +
                (row.id === selectedId ? ' datatable__row--selected' : '') +
                (row.muted ? ' datatable__row--muted' : '') +
                (expanded ? ' datatable__row--expanded' : '')
              }
              style={{ gridTemplateColumns: gridTemplate }}
              role="button"
              tabIndex={0}
              aria-expanded={renderExpanded ? expanded : undefined}
              onClick={(e) => {
                e.stopPropagation()
                onSelect(row.id)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelect(row.id)
                }
              }}
            >
              <div className="datatable__identity">
                {row.leading}
                <div className="datatable__identitytext">
                  <div className="datatable__name">
                    {row.title}
                    {row.flag && (
                      <span className={`datatable__flag datatable__flag--${row.flag}`}>!</span>
                    )}
                  </div>
                  <div className="datatable__meta">{row.subtitle}</div>
                </div>
              </div>

              {row.cells.map((cell) =>
                cell.node ? (
                  <div key={cell.key} className="datatable__cell" title={cell.title}>
                    {cell.node}
                  </div>
                ) : cell.badge ? (
                  <div key={cell.key} className="datatable__cell">
                    <span
                      className="datatable__badge"
                      style={{ background: cell.bg, color: cell.color }}
                      title={cell.title}
                    >
                      {cell.text}
                    </span>
                  </div>
                ) : (
                  <div
                    key={cell.key}
                    className={
                      'datatable__cell datatable__plain' +
                      (cell.italic ? ' datatable__plain--agreed' : '') +
                      (cell.mono ? ' datatable__plain--mono' : '') +
                      (cell.sub ? ' datatable__plain--stacked' : '')
                    }
                    style={cell.color ? { color: cell.color } : undefined}
                    title={cell.title ?? cell.text}
                  >
                    {cell.text}
                    {cell.sub && <span className="datatable__sub">{cell.sub}</span>}
                  </div>
                ),
              )}

              {showActions && (
                <div className="datatable__actions" onClick={(e) => e.stopPropagation()}>
                  {row.actions}
                </div>
              )}
            </div>

            {/* A click inside the panel must not reach the row, or filling in a
                field would collapse the thing being filled in. */}
            {expanded && (
              <div
                className="datatable__panel"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="region"
                aria-label={row.title}
              >
                {renderExpanded(row.id)}
              </div>
            )}
            </div>
            )
          })}

        {!loading && rows.length === 0 && (
          <div className="datatable__empty">{emptyNote ?? DEFAULT_EMPTY}</div>
        )}
      </div>

      {/* Outside the scrolling body, so it stays visible over a long register —
          a total you have to scroll to find is a total nobody reads. Hidden when
          there is nothing to add up, because "Total · 0 of 144" over an empty
          table says less than the empty state does. */}
      {totals && !loading && rows.length > 0 && (
        <div
          className="datatable__foot"
          style={{ gridTemplateColumns: gridTemplate, minWidth }}
        >
          <div className="datatable__foot-label">{totals.label}</div>
          {totals.cells.map((cell) => (
            <div
              key={cell.key}
              className={
                'datatable__foot-cell' +
                (cell.italic ? ' datatable__plain--agreed' : '') +
                (cell.sub ? ' datatable__plain--stacked' : '')
              }
              style={cell.color ? { color: cell.color } : undefined}
              title={cell.title}
            >
              {cell.text}
              {/* 79: subscribed shares are summed on their own line, never
                  added to the issued ones above them. */}
              {cell.sub && <span className="datatable__sub">{cell.sub}</span>}
            </div>
          ))}
          {showActions && <div />}
        </div>
      )}
    </div>
  )
}

export function ColumnPicker<K extends string>({
  meta,
  columns,
  onToggle,
  onClose,
}: {
  meta: Record<K, ColumnMeta>
  columns: K[]
  onToggle: (key: K) => void
  onClose: () => void
}) {
  return (
    <div className="colpicker" onClick={(e) => e.stopPropagation()}>
      {(Object.keys(meta) as K[]).map((key) => (
        <label key={key} className="colpicker__item">
          <input type="checkbox" checked={columns.includes(key)} onChange={() => onToggle(key)} />
          {meta[key].label}
        </label>
      ))}
      <button className="colpicker__done" onClick={onClose}>
        Done
      </button>
    </div>
  )
}
